import Fraction from 'fraction.js'
import LRU from 'lru-cache'

let cache = new LRU(100)

class FractionBased extends Fraction {
  toFloat () {
    return this.valueOf()
  }

  serialize () {
    return `${this.s * this.n}/${this.d}`
  }

  eq (other) {
    return this.compare(other) === 0
  }

  gt (other) {
    return this.compare(other) > 0
  }

  lt (other) {
    return this.compare(other) < 0
  }

  lte (other) {
    return this.compare(other) <= 0
  }

  gte (other) {
    return this.compare(other) >= 0
  }

  abs () {
    return new this.constructor(super.abs())
  }

  add (other) {
    return new this.constructor(super.add(other))
  }

  sub (other) {
    return new this.constructor(super.sub(other))
  }

  mul (other) {
    return new this.constructor(super.mul(other))
  }

  div (other) {
    return new this.constructor(super.div(other))
  }

  neg () {
    return new this.constructor(super.neg())
  }

  mod (other) {
    return new this.constructor(super.mod(other))
  }

  inverse () {
    return new this.constructor(super.inverse())
  }

  floor (...places) {
    return new this.constructor(super.floor(...places))
  }

  ceil (...places) {
    return new this.constructor(super.ceil(...places))
  }

  round (...places) {
    return new this.constructor(super.round(...places))
  }

  simplify (threshold) {
    return new this.constructor(super.simplify(threshold))
  }
}

export class Time extends FractionBased {
  toFrame (timebase) {
    return this.mul(timebase || this.refTimebase).floor().valueOf()
  }

  splitTime () {
    let time = this
    let hours = Math.floor(time.div(3600).valueOf())
    time = time.sub(hours * 3600)
    let minutes = Math.floor(time.div(60).valueOf())
    time = time.sub(minutes * 60)
    let seconds = Math.floor(time.valueOf())
    time = time.sub(seconds)
    return [hours, minutes, seconds, time]
  }

  adjustDropFrame (framenum, timebase) {
    const precision = 0.000000001
    timebase = new Time(timebase)
    timebase = timebase.simplify(precision)

    const dropInterval = new Time(30)
    const dropFrames = timebase.div(dropInterval).mul(2).floor()
    const framesPer10mins = timebase.div(dropInterval).mul(17982).floor()

    framenum = framenum.sub(framenum.div(18000).mul(18))
    let min10Drift = dropFrames
      .mul(9)
      .mul(framenum.div(framesPer10mins).floor())
    let min1Drift = dropFrames.mul(
      Time.max(Time.zero, framenum
        .mod(framesPer10mins)
        .floor()
        .sub(dropFrames)
        .div(framesPer10mins.div(10).floor())
        .floor())
    )

    return framenum.add(min10Drift).add(min1Drift)
  }

  adjustOffset (timebase) {
    const precision = 0.000000001
    timebase = new Time(timebase || 100)
    timebase = timebase.simplify(precision)

    if (timebase.d === 1001) {
      let isDF = !(timebase.n % 30000)
      if (isDF) {
        let framenum = this.mul(timebase)
        framenum = framenum.add(framenum.div(17982).mul(18))
        return framenum.div(timebase)
      }
    }

    return this
  }

  toString (timebase, format) {
    if (format === undefined) {
      format = 'full'
    } else if (format === false) {
      format = 'full-seconds'
    }

    const precision = 0.000000001
    timebase = new Time(timebase || 100)
    timebase = timebase.simplify(precision)

    let time = this
    let framenum = time.mul(timebase)

    let isDF = false
    if (timebase.d === 1001) {
      isDF = !(timebase.n % 30000)
      timebase = new Time(timebase.n, timebase.d - 1)
    }

    if (isDF) {
      framenum = time.mul(timebase)
      framenum = this.adjustDropFrame(framenum, timebase)
    }

    let ff = framenum.mod(timebase)
    let ss = framenum.div(timebase).mod(60)
    let mm = framenum.div(timebase.mul(60)).mod(60)
    let hh = framenum.div(timebase.mul(3600)).mod(24)

    let s = ''
    if (format !== 'short' || hh.gt(0)) {
      s += ('' + hh.floor()).padStart(2, '0') + ':'
    }
    s += ('' + mm.floor()).padStart(2, '0') + ':'
    s += ('' + ss.floor()).padStart(2, '0')

    if (format === 'full') {
      if (timebase.valueOf() === 100) {
        s += '.'
      } else {
        s += isDF ? ';' : ':'
      }

      s += ('' + ff.floor()).padStart(2, '0')
    }

    return s
  }

  static parse (json, refTimebase) {
    let t = new Time(json)
    t.refTimebase = refTimebase
    return t
  }

  clamp (lower, upper) {
    return Time.max(Time.min(this, upper), lower)
  }

  static min (...items) {
    return items.reduce((a, b) => a.gt(b) ? b : a)
  }

  static max (...items) {
    return items.reduce((a, b) => a.lt(b) ? b : a)
  }

  static cached (t) {
    let v = cache.get(t.valueOf())
    if (v) {
      return v
    }
    cache.set(t.valueOf(), t)
    return t
  }
}

Time.zero = new Time(0, 1)
Time.infinity = new Time(999999999, 1)

export class Framerate extends FractionBased {
  toFPS () {
    return this.inverse().valueOf()
  }

  static parse (json) {
    return new Framerate(json)
  }
}
